import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Crown, 
  Users, 
  Star,
  Clock,
  Award,
  BookOpen
} from 'lucide-react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { saveTimelineNavigationState } from '../utils/timelineNavigationState';

const DynastyDetailContainer = styled.div`
  min-height: 100vh;
  background: var(--color-background);
  padding: var(--spacing-xl) 0;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  margin-bottom: var(--spacing-xl);
  transition: all var(--animation-duration) ease;
  
  &:hover {
    background: var(--color-surface);
    color: var(--color-primary);
    border-color: var(--color-primary);
  }
`;

const Header = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-2xl);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const DynastyTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const DynastyPeriod = styled.p`
  font-size: 1.25rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-lg);
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--color-text-muted);
  
  svg {
    width: 20px;
    height: 20px;
    color: var(--color-primary);
  }
  
  .label {
    font-weight: 500;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const Section = styled(motion.div)`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--color-primary);
  }
`;

const Description = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin: 0;
`;

const KnowledgeSummary = styled.p`
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.6;
  margin: -12px 0 var(--spacing-md);
`;

const KnowledgeList = styled.div`
  display: flex;
  flex-direction: column;
`;

const KnowledgeTopic = styled.section`
  padding: var(--spacing-lg) 0;
  border-top: 1px solid var(--color-border);

  &:first-child {
    padding-top: 0;
    border-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

const KnowledgeTopicTitle = styled.h3`
  color: var(--color-text-primary);
  font-size: 1.05rem;
  font-weight: 650;
  margin: 0 0 var(--spacing-xs);
`;

const KnowledgeTopicSummary = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin: 0 0 var(--spacing-sm);
`;

const KnowledgePoints = styled.ul`
  display: grid;
  gap: var(--spacing-xs);
  margin: 0;
  padding-left: 1.25rem;
  color: var(--color-text-secondary);
  line-height: 1.65;

  li::marker {
    color: var(--color-primary);
  }
`;

const EventList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const EventItem = styled.li`
  padding: var(--spacing-md) 0;
  border-bottom: 1px solid var(--color-border);
  
  &:last-child {
    border-bottom: none;
  }
  
  .event-year {
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: var(--spacing-xs);
  }
  
  .event-title {
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: var(--spacing-xs);
  }
  
  .event-line {
    margin: 8px 0;
  }
  
  .event-label {
    font-weight: 700;
    color: var(--color-primary);
    margin-right: var(--spacing-xs);
  }
  
  .event-description {
    color: var(--color-text-secondary);
    line-height: 1.5;
  }
`;

const EmperorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const EmperorItem = styled.div`
  padding: var(--spacing-md);
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  
  .emperor-name {
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: var(--spacing-xs);
  }
  
  .emperor-reign {
    color: var(--color-text-muted);
    font-size: 0.875rem;
    margin-bottom: var(--spacing-sm);
  }

  .emperor-subtitle {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
    margin-bottom: var(--spacing-sm);
  }

  .emperor-section-title {
    font-weight: 600;
    color: var(--color-text-primary);
    margin: var(--spacing-sm) 0;
  }

  .emperor-basic {
    color: var(--color-text-secondary);
    line-height: 1.6;
    font-size: 0.95rem;
  }

  .emperor-bio {
    color: var(--color-text-secondary);
    line-height: 1.6;
    margin-top: var(--spacing-xs);
  }

  .emperor-achievements {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .emperor-achievement-item {
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-sm);
    padding: var(--spacing-xs) 0;
    color: var(--color-text-secondary);
  }
`;

const AchievementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AchievementItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) 0;
  
  &::before {
    content: '•';
    color: var(--color-primary);
    font-weight: bold;
    margin-top: 2px;
  }
  
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 辅助：解析并渲染事件描述为分行（加粗标签）
const parseEventDescription = (desc) => {
  if (!desc || typeof desc !== 'string') return null;
  const regex = /(意义|影响|贡献\/危害|参与者)：/g;
  const matches = [];
  let m;
  while ((m = regex.exec(desc)) !== null) {
    matches.push({ label: m[1], index: m.index, length: m[0].length });
  }
  if (matches.length === 0) return null;
  return matches.map((match, i) => {
    const start = match.index + match.length;
    const end = i + 1 < matches.length ? matches[i + 1].index : desc.length;
    const content = desc.slice(start, end).trim();
    return { label: match.label, content };
  });
};

const renderEventDescription = (desc) => {
  const segments = parseEventDescription(desc);
  if (!segments) return (<div className="event-description">{desc}</div>);
  return segments.map((seg, i) => (
    <div key={i} className="event-line">
      <span className="event-label">{seg.label}：</span>
      <span className="event-description">{seg.content}</span>
    </div>
  ));
};

const renderSources = (sources) => {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    <div className="event-description" style={{ marginTop: '8px', fontSize: '0.85rem' }}>
      <div style={{ fontWeight: 700, color: 'var(--color-primary)', marginBottom: '4px' }}>
        史实依据
      </div>
      <ul style={{ margin: 0, paddingLeft: '18px' }}>
        {sources.map((sourceItem, index) => {
          const title = sourceItem?.title || '';
          const reference = sourceItem?.reference ? `（${sourceItem.reference}）` : '';
          const url = sourceItem?.url;
          const note = sourceItem?.note ? `：${sourceItem.note}` : '';
          return (
            <li key={index} style={{ margin: '4px 0' }}>
              {url ? (
                <a href={url} target="_blank" rel="noreferrer">
                  {title}
                </a>
              ) : (
                <span>{title}</span>
              )}
              {reference}{note}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// 新增：统一年份显示（支持公元/公元前）
const formatYear = (year) => {
  if (typeof year !== 'number') return String(year);
  return year < 0 ? `公元前${Math.abs(year)}年` : `公元${year}年`;
};

const getEmperorBasicInfo = (emperor) => [
  typeof emperor.birthYear === 'number' && typeof emperor.deathYear === 'number'
    ? `生卒：${formatYear(emperor.birthYear)} - ${formatYear(emperor.deathYear)}`
    : '',
  emperor.personalName ? `姓名：${emperor.personalName}` : '',
  emperor.courtesyName ? `字：${emperor.courtesyName}` : '',
  emperor.birthPlace ? `籍贯：${emperor.birthPlace}` : '',
  Array.isArray(emperor.eraNames) && emperor.eraNames.length > 0
    ? `年号：${emperor.eraNames.join('、')}`
    : ''
].filter(Boolean);

const DynastyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.timelineSnapshot) {
      saveTimelineNavigationState(location.state.timelineSnapshot);
    }
  }, [location.state]);

  const returnToTimeline = () => {
    if (location.state?.fromTimeline) {
      navigate(-1);
      return;
    }

    navigate('/timeline');
  };

  const { data: dynasty, isLoading, error } = useQuery(
    ['dynasty', id],
    async () => {
      const response = await axios.get(`/api/dynasties/${id}`);
      return response.data.data;
    },
    {
      enabled: !!id
    }
  );

  if (isLoading) {
    return (
      <DynastyDetailContainer>
        <Container>
          <LoadingContainer>
            <LoadingSpinner />
            <p>正在加载条目信息...</p>
          </LoadingContainer>
        </Container>
      </DynastyDetailContainer>
    );
  }

  if (error || !dynasty) {
    return (
      <DynastyDetailContainer>
        <Container>
          <BackButton onClick={returnToTimeline}>
            <ArrowLeft />
            返回时间线
          </BackButton>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>条目信息不存在或加载失败</p>
          </div>
        </Container>
      </DynastyDetailContainer>
    );
  }

  const declineReasons = Array.isArray(dynasty.declineReasons)
    ? dynasty.declineReasons
    : (typeof dynasty.decline === 'string' && dynasty.decline.trim() ? [dynasty.decline.trim()] : []);

  return (
    <DynastyDetailContainer>
      <Container>
        <BackButton onClick={returnToTimeline}>
          <ArrowLeft />
          返回时间线
        </BackButton>

        <Header>
          <DynastyTitle>{dynasty.name}</DynastyTitle>
          <DynastyPeriod>
            {formatYear(dynasty.startYear).replace('年','')} - {formatYear(dynasty.endYear).replace('年','')} 
            ({dynasty.duration}年)
          </DynastyPeriod>
          
          <MetaGrid>
            <MetaItem>
              <MapPin />
              <span className="label">首都:</span>
              <span>{dynasty.capital}</span>
            </MetaItem>
            <MetaItem>
              <Crown />
              <span className="label">创建者:</span>
              <span>{dynasty.founder}</span>
            </MetaItem>
            <MetaItem>
              <Calendar />
              <span className="label">持续时间:</span>
              <span>{dynasty.duration}年</span>
            </MetaItem>
            <MetaItem>
              <Users />
              <span className="label">人物收录:</span>
              <span>{dynasty.emperors?.length || 0}位</span>
            </MetaItem>
          </MetaGrid>
        </Header>

        <ContentGrid>
          <MainContent>
            <Section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <SectionTitle>
                <Star />
                条目概述
              </SectionTitle>
              <Description>{dynasty.description}</Description>
            </Section>

            {Array.isArray(dynasty.knowledgeSections) && dynasty.knowledgeSections.length > 0 && (
              <Section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 }}
              >
                <SectionTitle>
                  <BookOpen />
                  核心知识
                </SectionTitle>
                <KnowledgeSummary>
                  {dynasty.knowledgeSections.length} 个专题 · {' '}
                  {dynasty.knowledgeSections.reduce(
                    (total, section) => total + (Array.isArray(section.points) ? section.points.length : 0),
                    0
                  )} 个知识点
                </KnowledgeSummary>
                <KnowledgeList>
                  {dynasty.knowledgeSections.map((section, index) => (
                    <KnowledgeTopic key={`${section.title}-${index}`}>
                      <KnowledgeTopicTitle>{section.title}</KnowledgeTopicTitle>
                      {section.summary && (
                        <KnowledgeTopicSummary>{section.summary}</KnowledgeTopicSummary>
                      )}
                      {Array.isArray(section.points) && section.points.length > 0 && (
                        <KnowledgePoints>
                          {section.points.map((point, pointIndex) => (
                            <li key={pointIndex}>{point}</li>
                          ))}
                        </KnowledgePoints>
                      )}
                    </KnowledgeTopic>
                  ))}
                </KnowledgeList>
              </Section>
            )}

            {dynasty.majorEvents && dynasty.majorEvents.length > 0 && (
              <Section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <SectionTitle>
                  <Clock />
                  重大事件
                </SectionTitle>
                <EventList>
                  {dynasty.majorEvents.map((event, index) => (
                    <EventItem key={index}>
                      <div className="event-year">{formatYear(event.year)}</div>
                      <div className="event-title">{event.event}</div>
                      {event.description && renderEventDescription(event.description)}
                      {renderSources(event.sources)}
                    </EventItem>
                  ))}
                </EventList>
              </Section>
            )}

            {dynasty.achievements && dynasty.achievements.length > 0 && (
              <Section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <SectionTitle>
                  <Award />
                  主要成就
                </SectionTitle>
                <AchievementList>
                  {dynasty.achievements.map((achievement, index) => (
                    <AchievementItem key={index}>
                      {achievement}
                    </AchievementItem>
                  ))}
                </AchievementList>
              </Section>
            )}
          </MainContent>

          <Sidebar>
            {dynasty.emperors && dynasty.emperors.length > 0 && (
              <Section
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <SectionTitle>
                  <Crown />
                  关键人物
                </SectionTitle>
                <EmperorList>
                  {dynasty.emperors.map((emperor, index) => {
                    const basicInfo = getEmperorBasicInfo(emperor);

                    return (
                      <EmperorItem key={index}>
                        <div className="emperor-name">{emperor.name}</div>
                        {typeof emperor.reignStart === 'number' && typeof emperor.reignEnd === 'number' && (
                          <div className="emperor-reign">
                            在位: {formatYear(emperor.reignStart)} - {formatYear(emperor.reignEnd)}
                          </div>
                        )}
                      {(emperor.templeName || emperor.posthumousName) && (
                        <div className="emperor-subtitle">
                          {emperor.templeName ? `庙号：${emperor.templeName} ` : ''}
                          {emperor.posthumousName ? `谥号：${emperor.posthumousName}` : ''}
                        </div>
                      )}

                      {basicInfo.length > 0 && (
                        <>
                          <div className="emperor-section-title">基础信息</div>
                          <div className="emperor-basic">{basicInfo.join('；')}</div>
                        </>
                      )}

                      {(emperor.biography || emperor.description) && (
                        <>
                          <div className="emperor-section-title">生平事迹</div>
                          <div className="emperor-bio">{emperor.biography || emperor.description}</div>
                        </>
                      )}

                      {(Array.isArray(emperor.achievementsDetailed) && emperor.achievementsDetailed.length > 0) && (
                        <>
                          <div className="emperor-section-title">在位期间的重大功绩</div>
                          <ul className="emperor-achievements">
                            {emperor.achievementsDetailed.map((achievementItem, i) => (
                              <li key={i} className="emperor-achievement-item">
                                <div style={{ flex: 1 }}>
                                  {achievementItem?.text || ''}
                                  {renderSources(achievementItem?.sources)}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      {(!emperor.achievementsDetailed || emperor.achievementsDetailed.length === 0) &&
                        Array.isArray(emperor.achievements) && emperor.achievements.length > 0 && (
                        <>
                          <div className="emperor-section-title">在位期间的重大功绩</div>
                          <ul className="emperor-achievements">
                            {emperor.achievements.map((item, i) => (
                              <li key={i} className="emperor-achievement-item">{item}</li>
                            ))}
                          </ul>
                        </>
                      )}

                      {Array.isArray(emperor.keyEvents) && emperor.keyEvents.length > 0 && (
                        <>
                          <div className="emperor-section-title">关键事迹</div>
                          <EventList>
                            {emperor.keyEvents.map((eventItem, eventIndex) => (
                              <EventItem key={eventIndex}>
                                <div className="event-year">{formatYear(eventItem.year)}</div>
                                <div className="event-title">{eventItem.title}</div>
                                {eventItem.description && renderEventDescription(eventItem.description)}
                                {renderSources(eventItem.sources)}
                              </EventItem>
                            ))}
                          </EventList>
                        </>
                      )}
                      </EmperorItem>
                    );
                  })}
                </EmperorList>
              </Section>
            )}

            {dynasty.culturalContributions && dynasty.culturalContributions.length > 0 && (
              <Section
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <SectionTitle>
                  <Star />
                  文化贡献
                </SectionTitle>
                <AchievementList>
                  {dynasty.culturalContributions.map((contribution, index) => (
                    <AchievementItem key={index}>
                      {contribution}
                    </AchievementItem>
                  ))}
                </AchievementList>
              </Section>
            )}

            {declineReasons.length > 0 && (
              <Section
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <SectionTitle>
                  <Clock />
                  阶段转折
                </SectionTitle>
                <AchievementList>
                  {declineReasons.map((reason, index) => (
                    <AchievementItem key={index}>
                      {reason}
                    </AchievementItem>
                  ))}
                </AchievementList>
              </Section>
            )}
          </Sidebar>
        </ContentGrid>
      </Container>
    </DynastyDetailContainer>
  );
};

export default DynastyDetail;
